import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DollarSign, Plus, Trash2 } from 'lucide-react';
import { AppBreadcrumbs } from '@/components/layout/AppBreadcrumbs';
import { PageContainer } from '@/components/layout/PageContainer';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PageState } from '@/components/layout/PageState';
import { budgetService } from '@/lib/api/budget.service';
import { handleApiError } from '@/lib/api/client';
import type { BudgetWorkspace } from '@/types/budget.types';

const formatCurrency = (amount: number, currency: string): string => {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  } catch {
    return `${currency} ${Number(amount || 0).toFixed(0)}`;
  }
};

export const BudgetWorkspacePage: React.FC = () => {
  const navigate = useNavigate();
  const { celebrationId } = useParams<{ celebrationId: string }>();
  const celebrationIdNum = Number.parseInt(celebrationId || '', 10);

  const [workspace, setWorkspace] = useState<BudgetWorkspace | null>(null);
  const [loading, setLoading] = useState(false);
  const [savingBudget, setSavingBudget] = useState(false);
  const [savingLineItem, setSavingLineItem] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [currencyInput, setCurrencyInput] = useState('USD');
  const [titleInput, setTitleInput] = useState('Primary Budget');
  const [totalBudgetInput, setTotalBudgetInput] = useState('0');
  const [labelInput, setLabelInput] = useState('');
  const [categoryInput, setCategoryInput] = useState('');
  const [estimatedInput, setEstimatedInput] = useState('0');
  const [editingLineItemId, setEditingLineItemId] = useState<number | null>(null);
  const [editingDraft, setEditingDraft] = useState<{
    label: string;
    category_id: string;
    estimated_amount: string;
    status: string;
  }>({
    label: '',
    category_id: '',
    estimated_amount: '0',
    status: 'planned',
  });

  const loadWorkspace = useCallback(async () => {
    if (!Number.isFinite(celebrationIdNum)) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await budgetService.getWorkspace(celebrationIdNum);
      setWorkspace(data);
      setCurrencyInput(data.budget.currency || 'USD');
      setTitleInput(data.budget.title || 'Primary Budget');
      setTotalBudgetInput(String(data.summary.estimated_total || 0));
      setCategoryInput(data.categories[0]?.id ? String(data.categories[0].id) : '');
      setEditingLineItemId(null);
    } catch (err: unknown) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  }, [celebrationIdNum]);

  React.useEffect(() => {
    void loadWorkspace();
  }, [loadWorkspace]);

  const summaryTiles = useMemo(() => {
    if (!workspace) {
      return [];
    }

    const summary = workspace.summary;
    const currency = summary.currency || workspace.budget.currency || 'USD';
    return [
      { label: 'Estimated', value: formatCurrency(summary.estimated_total, currency) },
      { label: 'Committed', value: formatCurrency(summary.committed_total, currency) },
      { label: 'Paid', value: formatCurrency(summary.paid_total, currency) },
      { label: 'Outstanding', value: formatCurrency(summary.outstanding_total, currency) },
    ];
  }, [workspace]);

  const handleSaveBudget = async () => {
    if (!Number.isFinite(celebrationIdNum)) {
      return;
    }

    setSavingBudget(true);
    setError(null);
    try {
      const totalBudget = Number.parseFloat(totalBudgetInput);
      if (Number.isNaN(totalBudget) || totalBudget < 0) {
        setError('Total budget must be a non-negative number.');
        setSavingBudget(false);
        return;
      }

      const updated = await budgetService.upsertBudget(celebrationIdNum, {
        currency: currencyInput.trim().toUpperCase(),
        title: titleInput.trim(),
        total_budget: totalBudget,
      });
      setWorkspace(updated);
      setTotalBudgetInput(String(updated.summary.estimated_total || 0));
    } catch (err: unknown) {
      setError(handleApiError(err));
    } finally {
      setSavingBudget(false);
    }
  };

  const handleCreateLineItem = async () => {
    if (!Number.isFinite(celebrationIdNum) || !workspace) {
      return;
    }

    const categoryId = categoryInput ? Number.parseInt(categoryInput, 10) : null;
    const estimated = Number.parseFloat(estimatedInput);
    if (!labelInput.trim() || Number.isNaN(estimated) || estimated < 0) {
      setError('Provide a label and non-negative estimated amount.');
      return;
    }

    setSavingLineItem(true);
    setError(null);
    try {
      const updated = await budgetService.createLineItem(celebrationIdNum, {
        label: labelInput.trim(),
        category_id: categoryId,
        estimated_amount: estimated,
        quantity: 1,
        unit_cost: estimated,
        status: 'planned',
      });

      setWorkspace(updated);
      setLabelInput('');
      setEstimatedInput('0');
      setCategoryInput(updated.categories[0]?.id ? String(updated.categories[0].id) : '');
    } catch (err: unknown) {
      setError(handleApiError(err));
    } finally {
      setSavingLineItem(false);
    }
  };

  const startEditingLineItem = (lineItemId: number) => {
    if (!workspace) {
      return;
    }

    const item = workspace.line_items.find((lineItem) => lineItem.id === lineItemId);
    if (!item) {
      return;
    }

    setEditingLineItemId(lineItemId);
    setEditingDraft({
      label: item.label,
      category_id: item.category_id ? String(item.category_id) : '',
      estimated_amount: String(item.estimated_amount ?? 0),
      status: item.status,
    });
  };

  const cancelEditingLineItem = () => {
    setEditingLineItemId(null);
    setEditingDraft({
      label: '',
      category_id: '',
      estimated_amount: '0',
      status: 'planned',
    });
  };

  const handleEditFieldChange = (field: 'label' | 'category_id' | 'estimated_amount' | 'status', value: string) => {
    setEditingDraft((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleUpdateLineItem = async (lineItemId: number) => {
    if (!Number.isFinite(celebrationIdNum)) {
      return;
    }

    if (editingLineItemId !== lineItemId) {
      return;
    }

    const patch = editingDraft;
    if (!patch || !patch.label.trim()) {
      setError('Line item label is required.');
      return;
    }

    const estimated = Number.parseFloat(patch.estimated_amount);
    if (Number.isNaN(estimated) || estimated < 0) {
      setError('Estimated amount must be a non-negative number.');
      return;
    }

    setSavingLineItem(true);
    setError(null);
    try {
      const updated = await budgetService.updateLineItem(celebrationIdNum, lineItemId, {
        label: patch.label.trim(),
        category_id: patch.category_id ? Number.parseInt(patch.category_id, 10) : null,
        estimated_amount: estimated,
        quantity: 1,
        unit_cost: estimated,
        status: patch.status as 'draft' | 'planned' | 'committed' | 'invoiced' | 'partially_paid' | 'paid' | 'cancelled',
      });
      setWorkspace(updated);
      cancelEditingLineItem();
    } catch (err: unknown) {
      setError(handleApiError(err));
    } finally {
      setSavingLineItem(false);
    }
  };

  const handleDeleteLineItem = async (lineItemId: number) => {
    if (!Number.isFinite(celebrationIdNum)) {
      return;
    }

    setSavingLineItem(true);
    setError(null);
    try {
      const updated = await budgetService.deleteLineItem(celebrationIdNum, lineItemId);
      setWorkspace(updated);
    } catch (err: unknown) {
      setError(handleApiError(err));
    } finally {
      setSavingLineItem(false);
    }
  };

  if (!Number.isFinite(celebrationIdNum)) {
    return (
      <PageContainer width="wide">
        <PageState kind="error" title="Budget Workspace" description="Invalid celebration ID." />
      </PageContainer>
    );
  }

  if (loading && !workspace) {
    return (
      <PageContainer width="wide">
        <PageState kind="loading" title="Budget Workspace" description="Loading budget workspace..." />
      </PageContainer>
    );
  }

  return (
    <>
      <AppBreadcrumbs />
      <PageContainer width="wide">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-teal-600" />
              Budget Workspace
            </h1>
            <p className="text-sm text-slate-500">Manage estimated, committed, and paid spend in one place.</p>
          </div>
          <Button variant="outline" onClick={() => navigate(`/celebrations/${celebrationIdNum}`)}>
            Back to Celebration
          </Button>
        </div>

        {error && (
          <div className="rounded-md bg-destructive/10 text-destructive px-4 py-2 text-sm mb-4">
            {error}
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          {summaryTiles.map((tile) => (
            <Card key={tile.label}>
              <CardContent compact>
                <div className="text-xs uppercase tracking-wide text-slate-500">{tile.label}</div>
                <div className="text-lg font-semibold text-slate-900">{tile.value}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card className="mb-5">
          <CardHeader compact>
            <CardTitle compact>Budget Settings</CardTitle>
          </CardHeader>
          <CardContent compact>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <label className="text-xs text-slate-600">Budget title</label>
                <Input value={titleInput} onChange={(event) => setTitleInput(event.target.value)} placeholder="Primary Budget" />
              </div>
              <div>
                <label className="text-xs text-slate-600">Currency</label>
                <Input value={currencyInput} onChange={(event) => setCurrencyInput(event.target.value.toUpperCase())} maxLength={3} placeholder="USD" />
              </div>
              <div>
                <label className="text-xs text-slate-600">Total budget</label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={totalBudgetInput}
                  onChange={(event) => setTotalBudgetInput(event.target.value)}
                  placeholder="0"
                />
              </div>
              <div className="flex items-end">
                <Button onClick={handleSaveBudget} disabled={savingBudget}>
                  {savingBudget ? 'Saving...' : 'Save Budget Settings'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader compact>
            <CardTitle compact>Line Items</CardTitle>
          </CardHeader>
          <CardContent compact>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
              <div className="md:col-span-2">
                <label className="text-xs text-slate-600">Label</label>
                <Input value={labelInput} onChange={(event) => setLabelInput(event.target.value)} placeholder="Catering deposit" />
              </div>
              <div>
                <label className="text-xs text-slate-600">Category</label>
                <select
                  className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={categoryInput}
                  onChange={(event) => setCategoryInput(event.target.value)}
                >
                  <option value="">Uncategorized</option>
                  {(workspace?.categories || []).map((category) => (
                    <option key={category.id} value={String(category.id)}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-600">Estimated amount</label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={estimatedInput}
                  onChange={(event) => setEstimatedInput(event.target.value)}
                  placeholder="0"
                />
              </div>
              <div className="flex items-end">
                <Button onClick={handleCreateLineItem} disabled={savingLineItem}>
                  <Plus className="h-4 w-4 mr-1" />
                  {savingLineItem ? 'Adding...' : 'Add Item'}
                </Button>
              </div>
            </div>

            {!workspace || workspace.line_items.length === 0 ? (
              <div className="text-sm text-slate-500">No budget line items yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-slate-500">
                      <th className="pb-2 pr-3 font-medium">Label</th>
                      <th className="pb-2 pr-3 font-medium">Category</th>
                      <th className="pb-2 pr-3 font-medium">Estimated</th>
                      <th className="pb-2 pr-3 font-medium">Committed</th>
                      <th className="pb-2 pr-3 font-medium">Paid</th>
                      <th className="pb-2 pr-3 font-medium">Status</th>
                      <th className="pb-2 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {workspace.line_items.map((item) => (
                      <tr key={item.id} className="border-b last:border-0">
                        <td className="py-2 pr-3 min-w-44">
                          {editingLineItemId === item.id ? (
                            <Input
                              value={editingDraft.label}
                              onChange={(event) => handleEditFieldChange('label', event.target.value)}
                            />
                          ) : (
                            <span className="font-medium text-slate-900">{item.label}</span>
                          )}
                        </td>
                        <td className="py-2 pr-3 min-w-40">
                          {editingLineItemId === item.id ? (
                            <select
                              className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                              value={editingDraft.category_id}
                              onChange={(event) => handleEditFieldChange('category_id', event.target.value)}
                            >
                              <option value="">Uncategorized</option>
                              {workspace.categories.map((category) => (
                                <option key={category.id} value={String(category.id)}>
                                  {category.name}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span className="text-slate-600">{item.category_name || 'Uncategorized'}</span>
                          )}
                        </td>
                        <td className="py-2 pr-3 min-w-36">
                          {editingLineItemId === item.id ? (
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={editingDraft.estimated_amount}
                              onChange={(event) => handleEditFieldChange('estimated_amount', event.target.value)}
                            />
                          ) : (
                            <span className="text-slate-700">{formatCurrency(item.estimated_amount, workspace.budget.currency)}</span>
                          )}
                        </td>
                        <td className="py-2 pr-3 text-slate-700">{formatCurrency(item.committed_amount, workspace.budget.currency)}</td>
                        <td className="py-2 pr-3 text-slate-700">{formatCurrency(item.paid_amount, workspace.budget.currency)}</td>
                        <td className="py-2 pr-3 min-w-40">
                          {editingLineItemId === item.id ? (
                            <select
                              className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                              value={editingDraft.status}
                              onChange={(event) => handleEditFieldChange('status', event.target.value)}
                            >
                              <option value="draft">Draft</option>
                              <option value="planned">Planned</option>
                              <option value="committed">Committed</option>
                              <option value="invoiced">Invoiced</option>
                              <option value="partially_paid">Partially paid</option>
                              <option value="paid">Paid</option>
                              <option value="cancelled">Cancelled</option>
                            </select>
                          ) : (
                            <span className="capitalize">{item.status.replace(/_/g, ' ')}</span>
                          )}
                        </td>
                        <td className="py-2 min-w-44">
                          {editingLineItemId === item.id ? (
                            <>
                              <Button
                                variant="secondary"
                                size="sm"
                                className="mr-2"
                                onClick={() => void handleUpdateLineItem(item.id)}
                                disabled={savingLineItem}
                              >
                                Save
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="mr-2"
                                onClick={cancelEditingLineItem}
                                disabled={savingLineItem}
                              >
                                Cancel
                              </Button>
                            </>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              className="mr-2"
                              onClick={() => startEditingLineItem(item.id)}
                              disabled={savingLineItem}
                            >
                              Edit
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleDeleteLineItem(item.id)}
                            disabled={savingLineItem}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </PageContainer>
    </>
  );
};
